import React, { useMemo, useState } from 'react';
import { FlockProfileDisplayTrait, getDisplayTraits } from './flock-profile-labels';
import { FlockProfileCardModel, FlockProfileTrait } from './flock-profile-types';

type Props = {
  model: FlockProfileCardModel;
  containerWidth: number;
};

type ChartVisualRow =
  | { type: 'group'; groupLabel: string; color: string }
  | {
    type: 'trait';
    displayTrait: FlockProfileDisplayTrait;
    color: string;
    showSubgroupLabel: boolean;
  };

type ChartTraitGroup = {
  groupLabel: string;
  color: string;
  traits: {
    trait: FlockProfileTrait;
    traitIndex: number;
  }[];
};

type GroupedAccuracyBlock = {
  key: string;
  label: string;
  color: string;
  startRowIndex: number;
  traitRowCount: number;
};

const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n));
const chartFontFamily = 'Chivo, Arial, sans-serif';
const chartBoldFontFamily = 'Chivo-Bold, Chivo, Arial, sans-serif';
const valueAccuracyGutter = 14;

type ChartColumnWidths = {
  leftWidth: number;
  traitCodeWidth: number;
  leftDescriptorWidth: number;
  rightDescriptorWidth: number;
  valueWidth: number;
  accuracyWidth: number;
};

const defaultColumnWidths: ChartColumnWidths = {
  leftWidth: 280,
  traitCodeWidth: 60,
  leftDescriptorWidth: 110,
  rightDescriptorWidth: 112,
  valueWidth: 45,
  accuracyWidth: 44,
};

const columnWidthClamp = {
  leftWidth: { min: 130, max: defaultColumnWidths.leftWidth },
  traitCodeWidth: { min: 42, max: defaultColumnWidths.traitCodeWidth },
  leftDescriptorWidth: { min: 68, max: defaultColumnWidths.leftDescriptorWidth },
  rightDescriptorWidth: { min: 78, max: defaultColumnWidths.rightDescriptorWidth },
  valueWidth: { min: 42, max: 56 },
  accuracyWidth: { min: 44, max: 58 },
};

const measureTextWidth = (text: string | undefined, font: string): number | null => {
  if (!text) {
    return 0;
  }

  if (typeof document === 'undefined') {
    return null;
  }

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  if (!context) {
    return null;
  }

  context.font = font;
  return context.measureText(text).width;
};

const measureMaxTextWidth = (values: string[], font: string): number | null => {
  let maxWidth = 0;

  for (const value of values) {
    const width = measureTextWidth(value, font);

    if (width === null) {
      return null;
    }

    maxWidth = Math.max(maxWidth, width);
  }

  return maxWidth;
};

const clampColumnWidth = (
  measuredWidth: number,
  padding: number,
  limits: { min: number; max: number },
): number => clamp(Math.ceil(measuredWidth + padding), limits.min, limits.max);

const getAccuracyScore = (label: string | undefined): number | null => {
  const normalisedLabel = label?.trim().toLowerCase();

  switch (normalisedLabel) {
    case 'high':
      return 3;
    case 'medium':
      return 2;
    case 'low':
      return 1;
    default:
      return null;
  }
};

const getGroupedAccuracyLabel = (traits: FlockProfileTrait[]): string => {
  const scores = traits
    .map((trait) => getAccuracyScore(trait.accuracyLabel))
    .filter((score): score is number => score !== null);

  if (scores.length === 0) {
    return '-';
  }

  const average = scores.reduce((total, score) => total + score, 0) / scores.length;

  if (average >= 2.5) {
    return 'High';
  }

  if (average >= 1.5) {
    return 'Medium';
  }

  return 'Low';
};

const getAccuracyColours = (label: string): { background: string; text: string } => {
  switch (label.trim().toLowerCase()) {
    case 'low':
      return {
        background: '#FDE7E7',
        text: '#C62828',
      };
    case 'medium':
      return {
        background: '#FFF1D6',
        text: '#B65C00',
      };
    case 'high':
      return {
        background: '#E6F4EA',
        text: '#1E7D34',
      };
    default:
      return {
        background: '#F2F3F5',
        text: '#6f7276',
      };
  }
};

const getGroupedAccuracyBlocks = (visualRows: ChartVisualRow[]): GroupedAccuracyBlock[] => {
  const blocks: GroupedAccuracyBlock[] = [];
  let currentGroup: {
    groupLabel: string;
    color: string;
    groupRowIndex: number;
    traits: FlockProfileTrait[];
  } | null = null;

  const pushCurrentGroup = () => {
    if (!currentGroup || currentGroup.traits.length === 0) {
      return;
    }

    blocks.push({
      key: `${currentGroup.groupLabel}-${currentGroup.groupRowIndex}`,
      label: getGroupedAccuracyLabel(currentGroup.traits),
      color: currentGroup.color,
      startRowIndex: currentGroup.groupRowIndex + 1,
      traitRowCount: currentGroup.traits.length,
    });
  };

  visualRows.forEach((row, index) => {
    if (row.type === 'group') {
      pushCurrentGroup();
      currentGroup = {
        groupLabel: row.groupLabel,
        color: row.color,
        groupRowIndex: index,
        traits: [],
      };
      return;
    }

    currentGroup?.traits.push(row.displayTrait.trait);
  });

  pushCurrentGroup();

  return blocks;
};

const getMeasuredColumnWidths = (visualRows: ChartVisualRow[]): ChartColumnWidths => {
  const leftLabels: string[] = [];
  const traitCodes: string[] = [];
  const leftDescriptors: string[] = [];
  const rightDescriptors: string[] = [];
  const values = ['VAL'];
  const accuracies = ['ACC', 'High', 'Medium', 'Low', '-'];

  visualRows.forEach((row) => {
    if (row.type === 'group') {
      leftLabels.push(row.groupLabel.toUpperCase());
      return;
    }

    const { displayLabel, subgroupCode, subgroupLabel, trait } = row.displayTrait;

    leftLabels.push(displayLabel);
    traitCodes.push(trait.code);
    leftDescriptors.push(trait.leftLabel);
    rightDescriptors.push(trait.rightLabel);
    values.push(trait.value.toFixed(2));

    if (row.showSubgroupLabel && subgroupLabel) {
      leftLabels.push(`${subgroupLabel.toUpperCase()} ${subgroupCode ? `(${subgroupCode})` : ''}`);
    }
  });

  const measuredLeftLabels = Math.max(
    measureMaxTextWidth(leftLabels, `500 12px ${chartFontFamily}`) ?? -1,
    measureMaxTextWidth(leftLabels, `700 14px ${chartBoldFontFamily}`) ?? -1,
  );
  const measuredTraitCodes = measureMaxTextWidth(traitCodes, `700 12px ${chartBoldFontFamily}`);
  const measuredLeftDescriptors = measureMaxTextWidth(leftDescriptors, `500 10px ${chartFontFamily}`);
  const measuredRightDescriptors = measureMaxTextWidth(rightDescriptors, `500 10px ${chartFontFamily}`);
  const measuredValues = measureMaxTextWidth(values, `700 12px ${chartBoldFontFamily}`);
  const measuredAccuracies = measureMaxTextWidth(accuracies, `400 12px ${chartFontFamily}`);

  if (
    measuredLeftLabels < 0
    || measuredTraitCodes === null
    || measuredLeftDescriptors === null
    || measuredRightDescriptors === null
    || measuredValues === null
    || measuredAccuracies === null
  ) {
    return defaultColumnWidths;
  }

  return {
    leftWidth: clampColumnWidth(measuredLeftLabels, 18, columnWidthClamp.leftWidth),
    traitCodeWidth: clampColumnWidth(measuredTraitCodes, 18, columnWidthClamp.traitCodeWidth),
    leftDescriptorWidth: clampColumnWidth(measuredLeftDescriptors, 24, columnWidthClamp.leftDescriptorWidth),
    rightDescriptorWidth: clampColumnWidth(measuredRightDescriptors, 16, columnWidthClamp.rightDescriptorWidth),
    valueWidth: clampColumnWidth(measuredValues, 12, columnWidthClamp.valueWidth),
    accuracyWidth: clampColumnWidth(measuredAccuracies, 14, columnWidthClamp.accuracyWidth),
  };
};

const getPercentileLabel = (analysisName: string): string => {
  const trimmedAnalysisName = analysisName.trim();

  if (trimmedAnalysisName.length === 0) {
    return 'Percentile';
  }

  return `${trimmedAnalysisName} Percentile`;
};

export const FlockProfileChart: React.FC<Props> = ({ model, containerWidth }) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const rowHeight = 34;
  const topPadding = 70;
  const bottomPadding = 36;
  const minPlotWidth = 200;

  const visualRows = useMemo<ChartVisualRow[]>(() => {
    const groupMap = new Map<string, ChartTraitGroup>();
    const rows: ChartVisualRow[] = [];

    model.traits.forEach((trait, traitIndex) => {
      const groupLabel = trait.groupLabel || 'Other';

      if (!groupMap.has(groupLabel)) {
        groupMap.set(groupLabel, { groupLabel, color: trait.color || '#4b4f56', traits: [] });
      }

      groupMap.get(groupLabel)?.traits.push({ trait, traitIndex });
    });

    groupMap.forEach((group) => {
      let currentSubgroupLabel: string | undefined;

      rows.push({ type: 'group', groupLabel: group.groupLabel, color: group.color });
      getDisplayTraits(group.traits).forEach((displayTrait) => {
        const showSubgroupLabel = Boolean(displayTrait.subgroupLabel && displayTrait.subgroupLabel !== currentSubgroupLabel);

        if (displayTrait.subgroupLabel && displayTrait.subgroupLabel !== currentSubgroupLabel) {
          currentSubgroupLabel = displayTrait.subgroupLabel;
        } else if (!displayTrait.subgroupLabel) {
          currentSubgroupLabel = undefined;
        }

        rows.push({
          type: 'trait',
          displayTrait,
          color: group.color,
          showSubgroupLabel,
        });
      });
    });

    return rows;
  }, [model.traits]);

  const {
    leftWidth,
    traitCodeWidth,
    leftDescriptorWidth,
    rightDescriptorWidth,
    valueWidth,
    accuracyWidth,
  } = useMemo(() => getMeasuredColumnWidths(visualRows), [visualRows]);
  const groupedAccuracyBlocks = useMemo(() => getGroupedAccuracyBlocks(visualRows), [visualRows]);
  const traitLabelX = leftWidth;
  const fixedColumnsWidth = leftWidth + traitCodeWidth + leftDescriptorWidth + rightDescriptorWidth + valueWidth + valueAccuracyGutter + accuracyWidth;
  const plotWidth = Math.max(minPlotWidth, containerWidth - fixedColumnsWidth);
  const chartHeight = topPadding + bottomPadding + visualRows.length * rowHeight + 10;
  const totalWidth = fixedColumnsWidth + plotWidth;

  const plotX = leftWidth + traitCodeWidth + leftDescriptorWidth;
  const rightColumnsX = plotX + plotWidth;
  const rightDescriptorX = rightColumnsX + 8;
  const valueColumnX = rightColumnsX + rightDescriptorWidth + valueWidth;
  const accuracyColumnX = valueColumnX + valueAccuracyGutter + accuracyWidth;
  const accuracyBlockX = accuracyColumnX - accuracyWidth;
  const plotY = topPadding;
  const plotHeight = visualRows.length * rowHeight;

  const axisY = plotY + plotHeight;

  const xForPercentile = (percentile: number) => {
    const p = clamp(percentile, 0, 100);
    return plotX + ((100 - p) / 100) * plotWidth;
  };

  const darkenHex = (hex: string, amount: number = 0.3): string => {
    const safeHex = hex.replace('#', '');
    if (!/^[0-9a-fA-F]{6}$/.test(safeHex)) return hex;

    const toChannel = (start: number) => {
      const value = parseInt(safeHex.slice(start, start + 2), 16);
      const darkened = Math.max(0, Math.min(255, Math.round(value * (1 - amount))));
      return darkened.toString(16).padStart(2, '0');
    };

    return `#${toChannel(0)}${toChannel(2)}${toChannel(4)}`;
  };

  const lightenHex = (hex: string, amount: number = 0.72): string => {
    const safeHex = hex.replace('#', '');
    if (!/^[0-9a-fA-F]{6}$/.test(safeHex)) return hex;

    const toChannel = (start: number) => {
      const value = parseInt(safeHex.slice(start, start + 2), 16);
      const lightened = Math.max(0, Math.min(255, Math.round(value + ((255 - value) * amount))));
      return lightened.toString(16).padStart(2, '0');
    };

    return `#${toChannel(0)}${toChannel(2)}${toChannel(4)}`;
  };

  const saturateHex = (hex: string, amount: number = 0.3): string => {
    const safeHex = hex.replace("#", "");
    if (!/^[0-9a-fA-F]{6}$/.test(safeHex)) return hex;

    const r = parseInt(safeHex.slice(0, 2), 16) / 255;
    const g = parseInt(safeHex.slice(2, 4), 16) / 255;
    const b = parseInt(safeHex.slice(4, 6), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);

    let h = 0;
    let s = 0;
    const l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;

      s = l > 0.5
        ? d / (2 - max - min)
        : d / (max + min);

      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
      }

      h /= 6;
    }

    s = Math.max(0, Math.min(1, s + amount));

    const hueToRgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    let newR: number;
    let newG: number;
    let newB: number;

    if (s === 0) {
      newR = newG = newB = l;
    } else {
      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;

      newR = hueToRgb(p, q, h + 1 / 3);
      newG = hueToRgb(p, q, h);
      newB = hueToRgb(p, q, h - 1 / 3);
    }

    const toHex = (value: number) =>
      Math.round(value * 255).toString(16).padStart(2, "0");

    return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
  };

  const hoveredTrait = hoveredIndex !== null ? model.traits[hoveredIndex] : null;
  const hoveredRowIndex = hoveredIndex !== null ? visualRows.findIndex((row) => row.type === 'trait' && row.displayTrait.traitIndex === hoveredIndex) : null;
  const hoveredY = hoveredRowIndex !== null && hoveredRowIndex >= 0 ? plotY + hoveredRowIndex * rowHeight + rowHeight / 2 : null;
  const hoveredX = hoveredTrait && hoveredY !== null ? xForPercentile(hoveredTrait.percentile) : null;
  const tooltipWidth = 44;
  const tooltipHeight = 22;
  const tooltipX = hoveredX !== null
    ? Math.max(plotX, Math.min(hoveredX - tooltipWidth / 2, (plotX + plotWidth) - tooltipWidth))
    : null;
  const tooltipY = hoveredY !== null ? hoveredY - 34 : null;

  return (
    <svg viewBox={`0 0 ${totalWidth} ${chartHeight}`} width={totalWidth} fontFamily={chartFontFamily} role="img" aria-label="Flock profile percentile chart">
      <g>
        {visualRows.map((row, index) => {
          const y = plotY + index * rowHeight;
          const isSubgroupDivider = row.type === 'trait' && row.showSubgroupLabel && row.displayTrait.subgroupLabel;
          return (
            <line
              key={`row-${index}`}
              x1={plotX}
              y1={y}
              x2={plotX + plotWidth}
              y2={y}
              stroke={isSubgroupDivider && row.type === 'trait' ? lightenHex(row.color) : '#f8f8f8f8'}
              strokeDasharray={isSubgroupDivider && row.type === 'trait' ? '3' : 0}
              strokeWidth={isSubgroupDivider ? 1.5 : 1}
            />
          );
        })}

        {[5, 15, 25, 35, 45, 55, 65, 75, 85, 95].map((tick) => {
          const x = xForPercentile(tick);
          return <line key={`${tick}-grid`} x1={x} y1={plotY} x2={x} y2={plotY + plotHeight} stroke="#f8f8f8f8" strokeWidth="1" />;
        })}

        {hoveredY !== null && (
          <line x1={plotX} y1={hoveredY} x2={plotX + plotWidth} y2={hoveredY} stroke="#a4a7ab" strokeDasharray="3" strokeWidth={1} />
        )}
      </g>

      <g>
        {[0, 5, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map((tick) => {
          const x = xForPercentile(tick);
          const isReferenceLine = model.referenceLines?.some((line) => line.percentile === tick);
          if (!isReferenceLine) {
            return <line key={`${tick}-grid-strong`} x1={x} y1={plotY} x2={x} y2={plotY + plotHeight} stroke='#8c9094' strokeDasharray={tick === 50 ? '0' : '3'} strokeWidth={tick === 50 ? 1.5 : 1} />;
          }
          return null;
        })}

        <line x1={plotX} y1={axisY} x2={plotX + plotWidth} y2={axisY} stroke="#6a6f75" strokeWidth={1.25} />

        {model.referenceLines?.map((line, index) => {
          const x = xForPercentile(line.percentile);
          return <line key={`${line.percentile}-${index}`} x1={x} y1={plotY} x2={x} y2={plotY + plotHeight} stroke={line.stroke} strokeDasharray={line.dashArray || '3'} strokeWidth={1.5} />;
        })}
      </g>

      <g>
        {visualRows.map((row, index) => {
          if (row.type !== 'trait') return null;
          const { trait, traitIndex } = row.displayTrait;
          const y = plotY + index * rowHeight + rowHeight / 2;
          const x0 = xForPercentile(50);
          const x1 = xForPercentile(trait.percentile);
          const barX = Math.min(x0, x1);
          const barWidth = Math.abs(x1 - x0);
          const gradientId = `trait-gradient-${traitIndex}`;
          const solidOnLeft = trait.percentile > 50;
          const traitColour = trait.color || '#4b4f56';

          return (
            <React.Fragment key={`${trait.code}-${traitIndex}`}>
              <defs>
                <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
                  {solidOnLeft ? (
                    <>
                      <stop offset="0%" stopColor={traitColour} stopOpacity={0.7} />
                      <stop offset="100%" stopColor={traitColour} stopOpacity={0.5} />
                    </>
                  ) : (
                    <>
                      <stop offset="0%" stopColor={saturateHex(darkenHex(traitColour))} stopOpacity={0.5} />
                      <stop offset="100%" stopColor={saturateHex(darkenHex(traitColour))} stopOpacity={0.9}/>
                    </>
                  )}
                </linearGradient>
              </defs>
              <rect x={barX} y={y - 9} width={Math.max(barWidth, 2)} height={18} fill={`url(#${gradientId})`} strokeWidth={0.8}/>
              <circle cx={x1} cy={y} r={8} fill="#fff" stroke={darkenHex(traitColour)} strokeWidth={2} onMouseEnter={() => setHoveredIndex(traitIndex)} onMouseLeave={() => setHoveredIndex(null)} />
            </React.Fragment>
          );
        })}
      </g>

      <g>
        {groupedAccuracyBlocks.map((block) => {
          const y = plotY + block.startRowIndex * rowHeight;
          const height = block.traitRowCount * rowHeight;
          const accuracyColours = getAccuracyColours(block.label);

          return (
            <g key={`accuracy-${block.key}`}>
              <rect
                x={accuracyBlockX}
                y={y}
                width={accuracyWidth}
                height={height}
                fill={accuracyColours.background}
              />
              <text
                x={accuracyBlockX + accuracyWidth / 2}
                y={y + height / 2 + 4}
                textAnchor="middle"
                fontFamily={chartBoldFontFamily}
                fontWeight={700}
                fontSize={11}
                letterSpacing={0.1}
                fill={accuracyColours.text}
              >
                {block.label}
              </text>
            </g>
          );
        })}
      </g>

      <g pointerEvents="none" style={{ opacity: hoveredTrait ? 1 : 0, transition: 'opacity 210ms ease' }}>
        {hoveredTrait && hoveredY !== null && hoveredX !== null && tooltipX !== null && tooltipY !== null && (
          <>
            <defs>
              <filter id="tooltipShadow" x="-20%" y="-50%" width="140%" height="200%">
                <feDropShadow dx="0" dy="1" stdDeviation="1.5" floodColor="#000" floodOpacity="0.18" />
              </filter>
            </defs>
            <rect x={tooltipX} y={tooltipY} width={tooltipWidth} height={tooltipHeight} rx={4} fill="#FFFFFF" filter={'url(#tooltipShadow)'} />
            <text x={tooltipX + tooltipWidth / 2} y={tooltipY + 15} textAnchor="middle" fontSize={12} fill="#4B4F54" fontFamily={chartBoldFontFamily} fontWeight={700}>{hoveredTrait.percentile}%</text>
          </>
        )}
      </g>

      <g>
        <text x={0} y={36} textAnchor="start" fontSize={25} letterSpacing={0.25} fontWeight={400} fill='#4B4F54'>Percentile Graph</text>
        <text x={valueColumnX} y={plotY + 4} fontFamily={chartBoldFontFamily} fontWeight={700} textAnchor="end" fontSize={12} letterSpacing={0.1}>VAL</text>
        <text x={accuracyBlockX + accuracyWidth / 2} y={plotY + 4} fontFamily={chartBoldFontFamily} fontWeight={700} textAnchor="middle" fontSize={11} letterSpacing={0.1}>ACC</text>
        {[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map((tick) => {
          const x = xForPercentile(tick);
          return <text key={`${tick}-label`} x={x} y={plotY + plotHeight + 22} textAnchor="middle" fontSize={11} letterSpacing={0.2} fill="#6f7276">{tick}</text>;
        })}

        {visualRows.map((row, index) => {
          const y = plotY + index * rowHeight + rowHeight / 2;
          if (row.type === 'group') {
            const groupColor = darkenHex(row.color);
            return (
              <g key={`group-${row.groupLabel}-${index}`}>
                <text x={leftWidth} y={y + 4} textAnchor="end" fontSize={14} fontFamily={chartBoldFontFamily} fontWeight={700} fill={groupColor}>{row.groupLabel.toUpperCase()}</text>
                <line x1={leftWidth + 12} y1={y} x2={totalWidth} y2={y} stroke={row.color} strokeDasharray="0" strokeWidth={1} />
              </g>
            );
          }

          const { trait, traitIndex, displayLabel } = row.displayTrait;
          const subgroupY = plotY + index * rowHeight;
          return (
            <g key={`${trait.code}-text-${traitIndex}`}>
              {row.showSubgroupLabel && row.displayTrait.subgroupLabel ? (
                <text x={leftWidth + leftDescriptorWidth - 12} y={subgroupY + 4} textAnchor="end" fontSize={8.5} fontFamily={chartBoldFontFamily} fontWeight={700} letterSpacing={0.1} fill={row.color}>
                  {`${row.displayTrait.subgroupLabel.toUpperCase()} ${row.displayTrait.subgroupCode && `(${row.displayTrait.subgroupCode})` || ''}`}
                </text>
              ) : null}
              <text x={traitLabelX} y={y + 4} textAnchor="end" fontSize={12} fontWeight={500} letterSpacing={0.1} fill="#6f7276">{` ${displayLabel}`}</text>
              <text x={traitLabelX + traitCodeWidth} textAnchor="end" y={y + 4} fontSize={12} fontFamily={chartBoldFontFamily} fontWeight={700} letterSpacing={0.1} fill="#6f7276">{`${trait.code} `}</text>
              <text x={leftWidth + leftDescriptorWidth + traitCodeWidth - 12} y={y + 4} fontWeight={500} textAnchor="end" fontSize={10} letterSpacing={0.1} fill="#6f7276">{trait.leftLabel}</text>
              <text x={rightDescriptorX} y={y + 4} fontSize={10} letterSpacing={0.1} fill="#6f7276">{trait.rightLabel}</text>
              <text x={valueColumnX} y={y + 4} fontFamily={chartBoldFontFamily} fontWeight={700} textAnchor="end" fontSize={12} letterSpacing={0.1}>{trait.value.toFixed(2)}</text>
            </g>
          );
        })}

        <text x={plotX + plotWidth / 2} y={chartHeight} textAnchor="middle" fontSize={11} fill="#6f7276">{getPercentileLabel(model.analysisName)}</text>
      </g>
    </svg>
  );
};
